import { useEffect, useCallback } from 'react';
import { useTracking } from 'react-tracking';

import type {
  ContentId,
  UserpilotEventData,
  UserpilotEventHandler,
} from '../types';

import { getUserpilotGlobal } from './getUserpilotGlobal';

const EVENTS = [
  'started',
  'dismissed',
  'completed',
  'step',
  'checklist_started',
  'checklist_completed',
  'checklist_dismissed',
] as const;

type Event = typeof EVENTS[number];
type UserpilotEventHandlers = Record<Event, UserpilotEventHandler>;

/**
 * Define handlers (and whether they are initialised) as a singleton, using module scope.
 */
let hasBoundHandlers = false;
const userpilotEventHandlers = new Map<
  ContentId,
  Partial<UserpilotEventHandlers>
>();

/**
 * useUserpilotEventHandlers: Assigns global event handlers for userpilot events
 * Event ref: https://docs.userpilot.com/article/61-javascript-api
 */
export function useUserpilotEventHandlers() {
  const userpilot = getUserpilotGlobal();
  const { trackEvent } = useTracking();

  const trackFn = useCallback(
    (eventName: Event, data: UserpilotEventData) => {
      const label = data.name ?? data.title ?? String(data.id);
      const formattedName = label.split(' ').join('_').toLowerCase();
      trackEvent({
        type: 'track',
        subject: `userpilot.${formattedName}`,
        action: eventName,
        ...data,
      });
    },
    [trackEvent]
  );

  const addEventHandler = useCallback(
    (
      { on, id }: { on: Event; id: ContentId },
      handler: UserpilotEventHandler
    ) => {
      const handlers = { ...userpilotEventHandlers.get(id) };
      userpilotEventHandlers.set(id, { ...handlers, [on]: handler });
    },
    []
  );

  const removeEventHandler = useCallback(
    ({ on, id }: { on: Event; id: ContentId }) => {
      const handlers = userpilotEventHandlers.get(id);
      if (handlers && on in handlers) {
        delete handlers[on];
        userpilotEventHandlers.set(id, handlers);
      }
    },
    []
  );

  const clearEventHandlers = useCallback((ids?: ContentId[]) => {
    if (ids?.length) {
      ids.forEach((id) => {
        userpilotEventHandlers.delete(id);
      });
    } else {
      userpilotEventHandlers.clear();
    }
  }, []);

  useEffect(() => {
    if (hasBoundHandlers || typeof userpilot === 'undefined') {
      return;
    }

    console.log('Userpilot: bind global event handlers');
    hasBoundHandlers = true;

    EVENTS.forEach((event) => {
      userpilot.on(
        event,
        ({ token, ...eventData }: UserpilotEventData & { token: string }) => {
          console.log(`Userpilot: event "${event}", content ${eventData.id}`);
          // Define tracking for event in one location
          trackFn(event, eventData);

          // for given content id, if there is a handler for this event, call the handler
          const handlers = { ...userpilotEventHandlers.get(eventData.id) };
          const handler = handlers[event];

          if (handler) {
            const removeSelf = () => {
              removeEventHandler({ on: event, id: eventData.id });
            };

            handler(eventData, removeSelf);
          }
        }
      );
    });
  }, [userpilot, removeEventHandler, trackFn]);

  return { addEventHandler, removeEventHandler, clearEventHandlers };
}
